import { Component, OnInit } from '@angular/core';
import { NzTableSortOrder } from 'ng-zorro-antd/table';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { MarketingQuotaService } from '../../services/marketing-quota.service';
import {
  ALLOCATION_COLUMNS,
  AllocationColumn,
  AllocationColumnDefinition,
  AllocationColumnKey,
  allocationValueCollator,
  AUDIENCE_SEGMENT_LABELS,
} from '../../models/marketing-quota.constants';

@Component({
  selector: 'app-marketing-quota',
  templateUrl: './marketing-quota.component.html',
  styleUrl: './marketing-quota.component.scss',
})
export class MarketingQuotaComponent implements OnInit {
  isLoading = false;
  headers: AllocationColumn[] = [];
  rawData: Record<string, unknown>[] = [];
  textFilters: Partial<Record<AllocationColumnKey, string>> = {};
  textFilterValues: Partial<Record<AllocationColumnKey, string>> = {};
  textFilterVisibility: Partial<Record<AllocationColumnKey, boolean>> = {};
  activeTextFilterKey: AllocationColumnKey | null = null;
  searchValue = '';
  pageIndex = 1;
  pageSize = 10;
  totalCount = 0;
  rows: Record<string, unknown>[] = [];
  sortKey: AllocationColumnKey | null = null;
  sortOrder: NzTableSortOrder = null;

  private hasMoreResults = true;
  private backendPageNumber = 1;
  private readonly backendPageSize = 10000;
  private lastTriggeredLoadedPages = 0;

  constructor(
    private marketingQuotaService: MarketingQuotaService,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    const orgId = this.userManagementService.getOrgId()?.toLowerCase();
    const columns = orgId === 'nfl' ? ALLOCATION_COLUMNS : ALLOCATION_COLUMNS.filter(({ key }) => key !== 'club_code');
    this.headers = columns.map((column) => this.createColumn(column));

    void this.fetchAllocations();
  }

  getValue(row: Record<string, unknown>, header: string): string {
    const value = String(row[header] ?? '-');
    return header === 'quota_type_code' ? (AUDIENCE_SEGMENT_LABELS[value] ?? value) : value;
  }

  get filteredData(): Record<string, unknown>[] {
    const activeFilters = Object.entries(this.textFilters).filter(([, value]) => value?.trim());

    if (!activeFilters.length) {
      return this.rawData;
    }

    return this.rawData.filter((row) =>
      activeFilters.every(([key, value]) =>
        this.getValue(row, key).toLowerCase().includes(value!.trim().toLowerCase()),
      ),
    );
  }

  applyTextFilter(key: AllocationColumnKey): void {
    this.textFilters[key] = this.textFilterValues[key]?.trim() ?? '';
    this.textFilterVisibility[key] = false;
    this.activeTextFilterKey = null;
  }

  resetTextFilter(key: AllocationColumnKey): void {
    this.textFilters[key] = '';
    this.textFilterValues[key] = '';
    this.textFilterVisibility[key] = false;
    this.activeTextFilterKey = null;
  }

  isTextFilterVisible(key: AllocationColumnKey): boolean {
    return this.textFilterVisibility[key] ?? false;
  }

  setTextFilterVisibility(key: AllocationColumnKey, isVisible: boolean): void {
    this.textFilterVisibility[key] = isVisible;
    this.activeTextFilterKey = isVisible ? key : null;
  }

  getActiveTextFilterLabel(): string {
    return this.headers.find(({ key }) => key === this.activeTextFilterKey)?.label ?? '';
  }

  getActiveTextFilterValue(): string {
    return this.activeTextFilterKey ? (this.textFilterValues[this.activeTextFilterKey] ?? '') : '';
  }

  setActiveTextFilterValue(value: string): void {
    if (this.activeTextFilterKey) {
      this.textFilterValues[this.activeTextFilterKey] = value;
    }
  }

  applyActiveTextFilter(): void {
    if (this.activeTextFilterKey) {
      this.applyTextFilter(this.activeTextFilterKey);
    }
  }

  resetActiveTextFilter(): void {
    if (this.activeTextFilterKey) {
      this.resetTextFilter(this.activeTextFilterKey);
    }
  }

  private async fetchAllocations(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      const result = await this.marketingQuotaService.getAllocations(this.backendPageSize, this.backendPageNumber, '');
      const parsedRows = this.parseRows(result?.rows).map((row) => this.filterAllocationRow(row));
      const backendPageSize = result?.pagination?.pageSize ?? this.backendPageSize;
      const backendPageNumber = result?.pagination?.pageNumber ?? this.backendPageNumber;

      this.rawData = parsedRows;
      this.hasMoreResults = parsedRows.length === backendPageSize;
      this.backendPageNumber = backendPageNumber + 1;
      this.updateTableRows();
      this.setColumnFilters();
    } catch {
      this.clearTable();
    } finally {
      this.isLoading = false;
    }
  }

  private clearTable(): void {
    this.rawData = [];
    this.setColumnFilters();
  }

  private parseRows(rows: string | null | undefined): Record<string, unknown>[] {
    if (!rows) {
      return [];
    }

    try {
      const parsedRows = JSON.parse(rows) as unknown;
      return Array.isArray(parsedRows) ? (parsedRows as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }

  private filterAllocationRow(row: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(ALLOCATION_COLUMNS.map(({ key }) => [key, row[key]]));
  }

  private createColumn(column: AllocationColumnDefinition): AllocationColumn {
    return {
      ...column,
      filters: [],
      filterFn:
        column.searchable || column.filterable === false
          ? null
          : (selectedValues: unknown[], row: Record<string, unknown>) =>
              !selectedValues.length || selectedValues.includes(this.getValue(row, column.key)),
      sortFn: (firstRow: Record<string, unknown>, secondRow: Record<string, unknown>) =>
        allocationValueCollator.compare(String(firstRow[column.key] ?? ''), String(secondRow[column.key] ?? '')),
    };
  }

  private setColumnFilters(): void {
    this.headers = this.headers.map((header) => ({
      ...header,
      filters:
        header.searchable || header.filterable === false
          ? []
          : header.staticFilters
            ? [...header.staticFilters]
                .sort((firstValue, secondValue) => allocationValueCollator.compare(firstValue, secondValue))
                .map((value) => {
                  const displayValue = this.getValue({ [header.key]: value }, header.key);
                  return { text: displayValue, value: displayValue };
                })
            : [
                ...new Set(
                  this.rawData
                    .filter((row) => {
                      const value = row[header.key];
                      return value != null && (typeof value !== 'string' || value.trim());
                    })
                    .map((row) => this.getValue(row, header.key)),
                ),
              ]
                .sort((firstValue, secondValue) => allocationValueCollator.compare(firstValue, secondValue))
                .map((value) => ({ text: value, value })),
    }));
  }

  async fetchBackendPage(): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      const result = await this.marketingQuotaService.getAllocations(this.backendPageSize, this.backendPageNumber, '');
      const parsedRows = this.parseRows(result?.rows).map((row) => this.filterAllocationRow(row));
      const backendPageSize = result?.pagination?.pageSize ?? this.backendPageSize;
      const backendPageNumber = result?.pagination?.pageNumber ?? this.backendPageNumber;

      this.rawData = [...this.rawData, ...parsedRows];
      this.updateTableRows();
      this.hasMoreResults = parsedRows.length === backendPageSize;
      this.backendPageNumber = backendPageNumber + 1;
    } catch {
      this.clearTable();
    } finally {
      this.isLoading = false;
    }
  }
  private getLoadedPageCount(): number {
    return Math.max(1, Math.ceil(this.rawData.length / this.pageSize));
  }

  private buildPageRows(): Record<string, unknown>[] {
    return this.getSortedRows();
    // @todo remove this later
    const filteredRows = this.getSortedRows();
    const startIndex = (this.pageIndex - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return filteredRows.slice(startIndex, endIndex);
  }

  private updateTableRows(): void {
    this.totalCount = this.getFilteredRows().length;
    this.rows = this.buildPageRows();
  }

  private getFilteredRows(): Record<string, unknown>[] {
    const searchText = this.searchValue.trim().toLowerCase();

    if (!searchText) {
      return this.rawData;
    }

    return this.rawData.filter((row) =>
      Object.values(row).some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(searchText),
      ),
    );
  }

  onPageIndexChange(pageIndex: number): void {
    if (pageIndex < 1 || this.isLoading || pageIndex === this.pageIndex) {
      return;
    }

    this.pageIndex = pageIndex;
    const loadedPages = this.getLoadedPageCount();

    if (pageIndex === loadedPages && this.hasMoreResults && loadedPages > this.lastTriggeredLoadedPages) {
      this.lastTriggeredLoadedPages = loadedPages;
      void this.fetchBackendPage();
      return;
    }

    this.updateTableRows();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.updateTableRows();
  }

  onSortChange(key: AllocationColumnKey, order: NzTableSortOrder): void {
    this.sortKey = order ? key : null;
    this.sortOrder = order;
    this.pageIndex = 1;
    this.updateTableRows();
  }

  getSortOrder(key: AllocationColumnKey): NzTableSortOrder {
    return this.sortKey === key ? this.sortOrder : null;
  }
  private getSortedRows(): Record<string, unknown>[] {
    const filteredRows = this.getFilteredRows();
    const { sortKey, sortOrder } = this;

    if (!sortKey || !sortOrder) {
      return filteredRows;
    }

    const sortedRows = [...filteredRows].sort((firstRow, secondRow) => {
      const comparison = allocationValueCollator.compare(
        String(firstRow[sortKey] ?? ''),
        String(secondRow[sortKey] ?? ''),
      );

      return sortOrder === 'ascend' ? comparison : -comparison;
    });

    return sortedRows;
  }
}
